<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: MediaCard                                             |
| Section: atoms                                                   |
| Purpose: Shows media thumbnail cards with title and metadata.    |
+------------------------------------------------------------------+
-->
<template>
    <div class="media_card" @click="handleClick" @mouseenter="applyRandomCardHover"
        @mouseleave="resetRandomCardHover">
        <div class="media_card_image" :style="{ backgroundImage: thumbnailUrl, transform: hoverTransform }">
            <span v-if="typeLabel" class="media_card_type">{{ typeLabel }}</span>
        </div>
        <p><span>{{ label }}</span><span class="date date--sm" v-if="showDate">{{ showDate }}</span></p>
        <p class="date" v-if="showDate">{{ showDate }}</p>
    </div>
</template>

<script>
import { useRandomCardHover } from '@/composables/useRandomCardHover';
import { getBackgroundImageUrl } from '@/utils/mediaUtils';

export default {
    name: 'MediaCard',
    setup() {
        return useRandomCardHover();
    },
    props: {
        label: {
            type: String,
            required: true
        },
        thumbnail: {
            type: String,
            required: true
        },
        url: {
            type: String,
            required: true
        },
        showDate: {
            type: String,
            default: null
        },
        typeLabel: {
            type: String,
            default: ''
        }
    },
    computed: {
        thumbnailUrl() {
            return getBackgroundImageUrl(this.thumbnail);
        }
    },
    methods: {
        handleClick() {
            // Emit an event to the parent to open the overlay
            this.$emit('click', { url: this.url });
        }
    }
}
</script>

<style scoped>
.media_card {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-self: stretch;
    align-items: flex-start;
    gap: 16px;
    flex-shrink: 0;
    position: relative;
    text-decoration: none;
    cursor: pointer;
    /* transform: translateY(-24px); */
    /* opacity: 0; */
    transition: all .6s cubic-bezier(.49, 1.42, .25, 1.53);
}

.media_card.visible {
    transform: translateY(0);
    opacity: 1;
}

.media_card:hover p:not(.date) {
    text-decoration: underline;
}

.media_card_image {
    position: relative;
    border-radius: 4px;
    border: 2px solid var(--brand-text-dark);
    background-repeat: no-repeat;
    background-size: 105%;
    background-position: center;
    box-shadow: var(--shadow-card-sm);
    width: 100%;
    padding-bottom: 64.86%;
    transition: var(--transition-bounce);
}

.media_card_type {
    position: absolute;
    top: 10px;
    left: 10px;
    padding: 4px 8px;
    border-radius: var(--border-radius-pill);
    background: rgba(255, 255, 255, .9);
    color: var(--brand-text-dark);
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    box-shadow: var(--shadow-card-sm);
}

.media_card:hover .media_card_image {
    background-size: 115%;
}

.media_card p {
    font-weight: 600;
    color: var(--brand-text-dark);
}

.media_card p span:nth-of-type(1) {
    display: -webkit-box;
    line-clamp: 3;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
}

.date {
    color: var(--brand-text-dark);
    opacity: .6;
    font-size: 14px;
    font-weight: 400 !important;
}

.media_card>.date:not(.date--sm) {
    margin-top: auto;
}

.date--sm {
    display: none;
}

@media (max-width: 860px) {
    .media_card p {
        display: inline-flex;
        flex-direction: column;
        gap: 0px;
    }

    .media_card>p:first-of-type {
        flex: 1 1 auto;
        align-self: stretch;
    }

    .media_card p span:nth-of-type(1) {
        line-clamp: 3;
        -webkit-line-clamp: 3;
    }

    .date:not(.date.date--sm) {
        display: none;
    }

    .date {
        display: block;
        opacity: .6;
    }

    .date--sm {
        margin-top: auto;
    }
}
</style>
